import { useState, useEffect } from 'react';
import Modal from '../common/Modal';
import { Button } from '../common/Button';
import { EnvelopeIcon, PaperAirplaneIcon, XMarkIcon, ArrowUturnLeftIcon, ArrowUturnRightIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import toast from 'react-hot-toast';

type CarrierInvitation = {
  carrier_id: number;
  carrier_name: string;
  recipient_emails: {
    main_email?: string;
    forwarder_emails?: Array<{name: string; email: string}>;
    driver_emails?: Array<{name: string; email: string}>;
  };
  status?: string;
  offer_price?: string;
  offer_currency?: string;
  vehicle_type?: string;
  delivery_eta?: string;
  notes?: string;
  responded_at?: string;
  selected?: boolean;
};

type Attachment = {
  filename: string;
  path: string;
  size: number;
  mimetype: string;
};

type Message = {
  id: number;
  transport_id?: number;
  carrier_id?: number;
  direction: 'outgoing' | 'incoming';
  sender_email?: string;
  recipient_email?: string;
  subject?: string;
  body?: string;
  attachments?: Attachment[];
  sent_at?: string;
  created_at?: string;
};

type Transport = {
  id: number;
  reference?: string;
  status: string;
  carrier_invitations?: CarrierInvitation[];
};

interface TimelineModalProps {
  isOpen: boolean;
  onClose: () => void;
  transport: Transport | null;
  messages: Message[];
  onRefresh: () => void;
}

type MessageThread = {
  carrierId: number | null;
  carrierName: string | null;
  messages: Message[];
  latestMessage: Message;
  count: number;
};

export const TimelineModal = ({
  isOpen,
  onClose,
  transport,
  messages,
  onRefresh
}: TimelineModalProps) => {
  const [selectedThread, setSelectedThread] = useState<number | null>(null);
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [replyMode, setReplyMode] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [composeMode, setComposeMode] = useState(false);
  const [selectedCarrier, setSelectedCarrier] = useState<string>('');
  const [messageSubject, setMessageSubject] = useState('');
  const [messageBody, setMessageBody] = useState('');
  const [sending, setSending] = useState(false);
  const [uploadedAttachments, setUploadedAttachments] = useState<Attachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (isOpen && transport && messages.length > 0) {
      setMessageSubject(`Dodatkowe informacje - Transport #${transport.id}`);
      // Auto-select first thread if exists
      const threads = createMessageThreads(messages);
      if (threads.length > 0 && selectedThread === null) {
        setSelectedThread(threads[0].carrierId);
        setSelectedMessage(threads[0].latestMessage);
      }
    }
  }, [isOpen, transport, messages]);

  if (!transport) return null;

  const carrierInvitations = transport.carrier_invitations || [];

  // Get carrier name by carrier_id
  const getCarrierNameById = (carrierId?: number | null): string | null => {
    if (!carrierId) return null;
    const invitation = carrierInvitations.find(inv => inv.carrier_id === carrierId);
    return invitation ? invitation.carrier_name : null;
  };

  // Group messages by carrier_id into threads
  const createMessageThreads = (msgs: Message[]): MessageThread[] => {
    // Group by carrier_id
    const grouped = msgs.reduce((acc, msg) => {
      const key = msg.carrier_id || 0; // Use 0 for unknown carriers
      if (!acc[key]) acc[key] = [];
      acc[key].push(msg);
      return acc;
    }, {} as Record<number, Message[]>);

    // Transform into thread objects
    const threads = Object.entries(grouped).map(([carrierIdStr, threadMessages]) => {
      const carrierId = parseInt(carrierIdStr);
      const carrierName = getCarrierNameById(carrierId === 0 ? null : carrierId);

      // Sort messages by date (newest first to find latest)
      const sortedMsgs = threadMessages.sort((a, b) => {
        const dateA = new Date(a.sent_at || a.created_at || 0).getTime();
        const dateB = new Date(b.sent_at || b.created_at || 0).getTime();
        return dateB - dateA;
      });

      return {
        carrierId: carrierId === 0 ? null : carrierId,
        carrierName,
        messages: sortedMsgs,
        latestMessage: sortedMsgs[0],
        count: sortedMsgs.length
      };
    });

    // Sort threads by latest message date (newest first)
    threads.sort((a, b) => {
      const dateA = new Date(a.latestMessage.sent_at || a.latestMessage.created_at || 0).getTime();
      const dateB = new Date(b.latestMessage.sent_at || b.latestMessage.created_at || 0).getTime();
      return dateB - dateA;
    });

    return threads;
  };

  const messageThreads = createMessageThreads(messages);

  // Get messages for selected thread (sorted chronologically for conversation view)
  const selectedThreadMessages = selectedThread !== null
    ? messageThreads.find(t => t.carrierId === selectedThread)?.messages.sort((a, b) => {
        const dateA = new Date(a.sent_at || a.created_at || 0).getTime();
        const dateB = new Date(b.sent_at || b.created_at || 0).getTime();
        return dateA - dateB; // Oldest first for conversation view
      }) || []
    : [];

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      // First check for new emails from IMAP
      const response = await api.post('/transport/check-emails', {});
      const newCount = response.data.newMessages || 0;

      if (newCount > 0) {
        toast.success(`Znaleziono ${newCount} nowych wiadomości`);
      } else {
        toast.success('Brak nowych wiadomości');
      }

      // Then reload messages for this transport
      await onRefresh();
    } catch (error: any) {
      console.error('Failed to refresh messages:', error);
      toast.error('Nie udało się odświeżyć wiadomości');
    } finally {
      setRefreshing(false);
    }
  };

  const handleFileUpload = async (files: FileList) => {
    const formData = new FormData();
    Array.from(files).forEach(file => {
      formData.append('files', file);
    });

    try {
      setUploading(true);
      const response = await api.post('/transport/messages/attachments', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const newAttachments = response.data.attachments;
      setUploadedAttachments(prev => [...prev, ...newAttachments]);
      toast.success(`Dodano ${newAttachments.length} załącznik(ów)`);
    } catch (error: any) {
      console.error('Failed to upload attachments:', error);
      toast.error('Nie udało się przesłać załączników');
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = (index: number) => {
    setUploadedAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSendMessage = async () => {
    if (!selectedCarrier || !messageBody.trim()) {
      toast.error('Wybierz przewoźnika i napisz treść wiadomości');
      return;
    }

    try {
      setSending(true);
      await api.post('/transport/messages/send-custom', {
        transportId: transport.id,
        recipientEmail: selectedCarrier,
        subject: messageSubject,
        body: messageBody,
        attachments: uploadedAttachments
      });

      toast.success('Wiadomość wysłana');
      setComposeMode(false);
      setMessageBody('');
      setSelectedCarrier('');
      setUploadedAttachments([]);
      onRefresh();
    } catch (error: any) {
      console.error('Failed to send message:', error);
      toast.error(error?.response?.data?.error || 'Nie udało się wysłać wiadomości');
    } finally {
      setSending(false);
    }
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || selectedThreadMessages.length === 0) {
      toast.error('Napisz treść odpowiedzi');
      return;
    }

    // Reply to the latest message in the thread
    const latestMessage = selectedThreadMessages[selectedThreadMessages.length - 1];

    try {
      setSending(true);
      await api.post('/transport/messages/reply', {
        messageId: latestMessage.id,
        replyText: replyText.trim(),
        attachments: uploadedAttachments
      });

      toast.success('Odpowiedź wysłana');
      setReplyMode(false);
      setReplyText('');
      setUploadedAttachments([]);
      onRefresh();
    } catch (error: any) {
      console.error('Failed to send reply:', error);
      toast.error(error?.response?.data?.error || 'Nie udało się wysłać odpowiedzi');
    } finally {
      setSending(false);
    }
  };

  // Get all unique carrier emails for dropdown
  const allCarrierEmails: Array<{email: string, name: string}> = [];
  carrierInvitations.forEach(inv => {
    if (inv.recipient_emails?.main_email) {
      allCarrierEmails.push({ email: inv.recipient_emails.main_email, name: inv.carrier_name });
    }
    inv.recipient_emails?.forwarder_emails?.forEach(f => {
      allCarrierEmails.push({ email: f.email, name: `${inv.carrier_name} - ${f.name}` });
    });
    inv.recipient_emails?.driver_emails?.forEach(d => {
      allCarrierEmails.push({ email: d.email, name: `${inv.carrier_name} - ${d.name}` });
    });
  });

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Strip HTML tags for preview
  const stripHtml = (html: string) => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Konwersacja z przewoźnikiem - Zlecenie #${transport.id}`}
      size="full"
    >
      <div className="flex h-[70vh]">
        {/* LEFT SIDEBAR - Message List */}
        <div className="w-1/3 border-r border-gray-300 flex flex-col">
          {/* Header */}
          <div className="p-4 border-b border-gray-300 bg-gray-50">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-800">
                Konwersacje ({messageThreads.length})
              </h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleRefresh}
                  disabled={refreshing}
                  className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-200 rounded-md transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Odśwież wiadomości"
                >
                  <svg className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span>{refreshing ? 'Sprawdzanie...' : 'Odśwież'}</span>
                </button>
                <Button
                  size="sm"
                  onClick={() => {
                    setComposeMode(true);
                    setSelectedThread(null);
                    setSelectedMessage(null);
                    setReplyMode(false);
                  }}
                  className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-1"
                >
                  <PaperAirplaneIcon className="w-4 h-4" />
                  Nowa
                </Button>
              </div>
            </div>
          </div>

          {/* Thread List */}
          <div className="flex-1 overflow-y-auto">
            {messageThreads.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <EnvelopeIcon className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p>Brak wiadomości</p>
                <p className="text-sm mt-1">Wyślij pierwszą wiadomość do przewoźnika</p>
              </div>
            ) : (
              messageThreads.map(thread => {
                const isSelected = selectedThread === thread.carrierId;
                const latestMsg = thread.latestMessage;
                const date = new Date(latestMsg.sent_at || latestMsg.created_at || '');
                const bodyPreview = latestMsg.body ? stripHtml(latestMsg.body) : '';

                return (
                  <div
                    key={thread.carrierId || 'unknown'}
                    onClick={() => {
                      setSelectedThread(thread.carrierId);
                      setSelectedMessage(thread.latestMessage);
                      setComposeMode(false);
                      setReplyMode(false);
                    }}
                    className={`p-4 border-b border-gray-200 cursor-pointer transition-colors ${
                      isSelected
                        ? 'bg-blue-100 border-l-4 border-l-blue-600'
                        : 'hover:bg-gray-50 border-l-4 border-l-transparent'
                    }`}
                  >
                    {/* Carrier Name & Message Count */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <EnvelopeIcon className="w-5 h-5 text-blue-600 flex-shrink-0" />
                        <span className="font-bold text-sm text-gray-900 truncate">
                          {thread.carrierName || 'Nieznany przewoźnik'}
                        </span>
                      </div>
                      <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full font-medium flex-shrink-0 ml-2">
                        {thread.count}
                      </span>
                    </div>

                    {/* Latest Message Subject */}
                    <div className="font-medium text-sm text-gray-900 mb-1 truncate">
                      {latestMsg.subject || '(Brak tematu)'}
                    </div>

                    {/* Latest Message Preview */}
                    <div className="text-xs text-gray-600 mb-2 line-clamp-2">
                      {truncateText(bodyPreview, 80)}
                    </div>

                    {/* Date */}
                    <div className="text-xs text-gray-500">
                      {date.toLocaleString('pl-PL', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* RIGHT PANEL - Message Details or Compose */}
        <div className="flex-1 flex flex-col">
          {composeMode ? (
            /* COMPOSE MODE */
            <div className="flex-1 flex flex-col">
              {/* Header */}
              <div className="p-4 border-b border-gray-300 bg-blue-50">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-800">Nowa wiadomość</h3>
                  <button
                    onClick={() => {
                      setComposeMode(false);
                      setMessageBody('');
                      setSelectedCarrier('');
                      if (messageThreads.length > 0) {
                        setSelectedThread(messageThreads[0].carrierId);
                        setSelectedMessage(messageThreads[0].latestMessage);
                      }
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Compose Form */}
              <div className="flex-1 p-6 overflow-y-auto bg-white">
                <div className="space-y-4 max-w-3xl">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Do (wybierz przewoźnika)
                    </label>
                    <select
                      value={selectedCarrier}
                      onChange={(e) => setSelectedCarrier(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">-- Wybierz przewoźnika --</option>
                      {allCarrierEmails.map((carrier, idx) => (
                        <option key={idx} value={carrier.email}>
                          {carrier.name} ({carrier.email})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Temat
                    </label>
                    <input
                      type="text"
                      value={messageSubject}
                      onChange={(e) => setMessageSubject(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Temat wiadomości"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Treść wiadomości
                    </label>
                    <textarea
                      value={messageBody}
                      onChange={(e) => setMessageBody(e.target.value)}
                      className="w-full h-64 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Treść wiadomości..."
                    />
                  </div>

                  {/* Attachments */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Załączniki
                    </label>
                    <input
                      type="file"
                      multiple
                      onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      disabled={uploading}
                    />
                    {uploading && <p className="text-sm text-gray-500 mt-1">Przesyłanie...</p>}
                    {uploadedAttachments.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {uploadedAttachments.map((att, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                            <div className="flex items-center gap-2 text-sm">
                              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                              </svg>
                              <span className="font-medium">{att.filename}</span>
                              <span className="text-gray-500">({formatFileSize(att.size)})</span>
                            </div>
                            <button
                              onClick={() => removeAttachment(idx)}
                              className="text-red-600 hover:text-red-800"
                              type="button"
                            >
                              <XMarkIcon className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={handleSendMessage}
                      disabled={sending || !messageBody.trim() || !selectedCarrier}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      {sending ? 'Wysyłanie...' : 'Wyślij wiadomość'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setComposeMode(false);
                        setMessageBody('');
                        setSelectedCarrier('');
                        if (messageThreads.length > 0) {
                          setSelectedThread(messageThreads[0].carrierId);
                          setSelectedMessage(messageThreads[0].latestMessage);
                        }
                      }}
                      disabled={sending}
                    >
                      Anuluj
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ) : selectedThread !== null && selectedThreadMessages.length > 0 ? (
            /* THREAD CONVERSATION VIEW */
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Header */}
              <div className="p-4 border-b border-gray-300 bg-gray-50">
                <div className="space-y-2">
                  <div className="text-lg font-semibold text-gray-900">
                    {getCarrierNameById(selectedThread) || 'Nieznany przewoźnik'}
                  </div>
                  <div className="text-sm text-gray-600">
                    {selectedThreadMessages.length} {selectedThreadMessages.length === 1 ? 'wiadomość' : 'wiadomości'}
                  </div>
                </div>
              </div>

              {/* Action Toolbar (macOS Mail style) */}
              <div className="px-4 py-2 border-b border-gray-200 bg-white flex items-center gap-2">
                <button
                  onClick={() => setReplyMode(true)}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                  title="Odpowiedz"
                >
                  <ArrowUturnLeftIcon className="w-4 h-4" />
                  <span>Odpowiedz</span>
                </button>
                <button
                  onClick={() => {
                    setComposeMode(true);
                    setSelectedThread(null);
                    setSelectedMessage(null);
                    setReplyMode(false);
                  }}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                  title="Przekaż"
                >
                  <ArrowUturnRightIcon className="w-4 h-4" />
                  <span>Przekaż</span>
                </button>
              </div>

              {/* Thread Messages with proper scroll */}
              <div className="flex-1 overflow-y-auto bg-gray-50">
                {!replyMode ? (
                  <div className="p-6 space-y-4">
                    {selectedThreadMessages.map((msg, index) => (
                      <div
                        key={msg.id}
                        className={`p-4 rounded-lg shadow-sm ${
                          msg.direction === 'outgoing'
                            ? 'bg-blue-50 border-l-4 border-blue-500'
                            : 'bg-green-50 border-l-4 border-green-500'
                        }`}
                      >
                        {/* Message Header */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            {msg.direction === 'outgoing' ? (
                              <PaperAirplaneIcon className="w-4 h-4 text-blue-600" />
                            ) : (
                              <EnvelopeIcon className="w-4 h-4 text-green-600" />
                            )}
                            <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                              msg.direction === 'outgoing'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-green-100 text-green-700'
                            }`}>
                              {msg.direction === 'outgoing' ? 'Wysłana' : 'Otrzymana'}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500">
                            {new Date(msg.sent_at || msg.created_at || '').toLocaleString('pl-PL', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        </div>

                        {/* Subject */}
                        <div className="font-medium text-sm text-gray-900 mb-2">
                          {msg.subject || '(Brak tematu)'}
                        </div>

                        {/* From/To */}
                        <div className="text-xs text-gray-600 mb-3">
                          <span className="font-medium">
                            {msg.direction === 'outgoing' ? 'Do: ' : 'Od: '}
                          </span>
                          {msg.direction === 'outgoing'
                            ? msg.recipient_email
                            : msg.sender_email}
                        </div>

                        {/* Body */}
                        <div
                          className="prose prose-sm max-w-none text-gray-800"
                          dangerouslySetInnerHTML={{ __html: msg.body || '<p class="text-gray-500 italic">Brak treści</p>' }}
                        />

                        {/* Attachments */}
                        {msg.attachments && msg.attachments.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <div className="text-xs font-medium text-gray-700 mb-2">
                              Załączniki ({msg.attachments.length}):
                            </div>
                            <div className="space-y-1">
                              {msg.attachments.map((att, attIdx) => (
                                <a
                                  key={attIdx}
                                  href={`${import.meta.env.VITE_API_URL || 'http://localhost:8000'}${att.path}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-2 p-2 bg-gray-100 hover:bg-gray-200 rounded text-sm transition-colors"
                                >
                                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                  </svg>
                                  <span className="font-medium text-blue-600">{att.filename}</span>
                                  <span className="text-gray-500">({formatFileSize(att.size)})</span>
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  /* Reply Form */
                  <div className="p-6">
                    <div className="mb-4">
                      <div className="text-sm font-medium text-gray-700 mb-2">
                        Odpowiadasz na ostatnią wiadomość:
                      </div>
                      <div
                        className="prose prose-sm max-w-none text-gray-800 mb-4 p-4 bg-white border-l-4 border-blue-500 rounded"
                        dangerouslySetInnerHTML={{
                          __html: selectedThreadMessages[selectedThreadMessages.length - 1].body ||
                          '<p class="text-gray-500 italic">Brak treści</p>'
                        }}
                      />
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Twoja odpowiedź
                        </label>
                        <textarea
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          className="w-full h-48 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Napisz odpowiedź..."
                          autoFocus
                        />
                      </div>

                      {/* Attachments */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Załączniki
                        </label>
                        <input
                          type="file"
                          multiple
                          onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
                          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                          disabled={uploading}
                        />
                        {uploading && <p className="text-sm text-gray-500 mt-1">Przesyłanie...</p>}
                        {uploadedAttachments.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {uploadedAttachments.map((att, idx) => (
                              <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                <div className="flex items-center gap-2 text-sm">
                                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                  </svg>
                                  <span className="font-medium">{att.filename}</span>
                                  <span className="text-gray-500">({formatFileSize(att.size)})</span>
                                </div>
                                <button
                                  onClick={() => removeAttachment(idx)}
                                  className="text-red-600 hover:text-red-800"
                                  type="button"
                                >
                                  <XMarkIcon className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="flex gap-2">
                        <Button
                          onClick={handleSendReply}
                          disabled={sending || !replyText.trim()}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          {sending ? 'Wysyłanie...' : 'Wyślij odpowiedź'}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setReplyMode(false);
                            setReplyText('');
                          }}
                          disabled={sending}
                        >
                          Anuluj
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* NO MESSAGE SELECTED */
            <div className="flex-1 flex items-center justify-center bg-gray-50">
              <div className="text-center text-gray-500">
                <EnvelopeIcon className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                <p className="text-lg font-medium">Wybierz wiadomość z listy</p>
                <p className="text-sm mt-1">lub utwórz nową wiadomość</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};
